import { useState, MouseEvent } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormLabel,
  Box,
  Select,
} from '@chakra-ui/react';
import {
  useChangeRoleMutation,
  useChangeEmploymentTypeMutation,
  useChangeManagerMutation,
  useChangeAdminMutation,
  useGetAllusersQuery,
  useGetUserByIdQuery,
} from '../../features/apis/authApi';

import ReactToastify from '../../hooks/useReactToastify';
import { FiUsers } from 'react-icons/fi';

type Props = {
  current_user?: {
    _id?: string;
    name?: string;
    email?: string;
    role?: string;
  };
  profile_user?: {
    _id?: string;
    name?: string;
    email?: string;
    role?: string;
    verified?: string;
    assigned_HR?: string;
    assigned_Manager?: string;
  };
  refetch: () => void;
  isOpen?: boolean;
  onClose?: () => void;
};

const selectClasses =
  'font-extrabold w-full h-[40px] px-4 rounded-[10px] bg-[rgba(196,196,196,0.22)] border-none outline-none font-[Nunito-Regular,sans-serif]';

const EmployeeAccessControl = ({
  current_user,
  profile_user,
  refetch,
  isOpen,
  onClose,
}: Props) => {
  const [role, setRole] = useState('Employee');
  const [employment_type, setEmploymentType] = useState('Permanent');
  const [manager, setManager] = useState('');
  const [admin, setAdmin] = useState('');

  const [roleLoading, setRoleLoading] = useState(false);
  const [empLoading, setEmpLoading] = useState(false);
  const [managerLoading, setManagerLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);

  const { data, isLoading: userLoading } = useGetUserByIdQuery(
    { _id: profile_user?._id },
    { skip: !profile_user?._id }
  );
  const { data: allUsers, isLoading: usersLoading } = useGetAllusersQuery();

  const [changeRole] = useChangeRoleMutation();
  const [changeEmploymentType] = useChangeEmploymentTypeMutation();
  const [changeManager] = useChangeManagerMutation();
  const [changeAdmin] = useChangeAdminMutation();

  const currentRole = data?.user?.role;
  const currentEmploymentType = data?.user?.employment_type;

  const employees = allUsers?.employee || [];

  const allEmployees = employees
    .filter((emp) => emp._id !== profile_user?._id)
    .map((emp) => ({
      value: emp._id,
      label: emp.name || emp.email,
    }));

  const currentManager = employees.find(
    (e) => e._id === profile_user?.assigned_Manager
  );

  const currentHR = employees.find((e) => e._id === profile_user?.assigned_HR);

  const resetForm = () => {
    setRole('Employee');
    setEmploymentType('Permanent');
    setManager('');
    setAdmin('');
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  if (!isOpen) {
    return null;
  }

  if (!current_user || !profile_user || userLoading || usersLoading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />
        <ModalContent
          maxH="90vh"
          overflowY="auto"
          borderWidth="0.5px"
          borderColor="#D4D4D4"
          mx={4}
        >
          <ModalHeader
            fontSize="sm"
            color="whiteAlpha.900"
            background="purple.500"
            borderTopRadius="md"
            py={3}
            px={4}
          >
            Update Employee Access
          </ModalHeader>
          <ModalCloseButton
            color="whiteAlpha.900"
            my={1}
            zIndex={2}
            sx={{
              _hover: {
                bg: 'transparent',
                color: 'white',
              },
            }}
          />
          <ModalBody pb={6}>
            <Box display="flex" flexDirection="column" gap={5}>
              {[...Array(4)].map((_, i) => (
                <Box key={i} className="space-y-2">
                  <div className="h-[13px] w-16 bg-gray-200 rounded-full animate-pulse" />
                  <div
                    className="h-[40px] w-full bg-gray-100 rounded-[10px] animate-pulse border border-[#D4D4D4]"
                    style={{ minHeight: '40px' }}
                  />
                  <div className="h-8 w-24 bg-gray-200 rounded-md animate-pulse mt-3" />
                </Box>
              ))}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // ---------------- ROLE ----------------
  const changeRoleHandler = async () => {
    setRoleLoading(true);
    try {
      const res = await changeRole({
        role,
        email: profile_user.email,
      }).unwrap();

      ReactToastify({
        message: res.log,
        success: res.status === '200',
      });
    } catch (err: any) {
      ReactToastify({
        message: err?.data?.log,
        success: false,
      });
    } finally {
      setRoleLoading(false);
      refetch();
    }
  };

  // ---------------- EMPLOYMENT TYPE ----------------
  const changeEmploymentHandler = async () => {
    setEmpLoading(true);
    try {
      const res = await changeEmploymentType({
        employment_type,
        email: profile_user.email,
      }).unwrap();

      ReactToastify({
        message: res.log,
        success: res.status === '200',
      });
    } catch (err: any) {
      ReactToastify({
        message: err?.data?.log,
        success: false,
      });
    } finally {
      setEmpLoading(false);
      refetch();
    }
  };

  // ---------------- MANAGER ----------------
  const changeManagerHandler = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setManagerLoading(true);

    try {
      const res = await changeManager({
        email: profile_user.email,
        manager_id: manager,
      }).unwrap();

      ReactToastify({
        message: res.log,
        success: res.status === '200',
      });

      setManager('');
      refetch();
    } catch (err: any) {
      ReactToastify({
        message: err?.data?.log || 'Failed to change manager',
        success: false,
      });
    } finally {
      setManagerLoading(false);
    }
  };

  // ---------------- HR ----------------
  const changeAdminHandler = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAdminLoading(true);

    try {
      const res = await changeAdmin({
        email: profile_user.email,
        admin_id: admin,
      }).unwrap();

      ReactToastify({
        message: res.log,
        success: res.status === '200',
      });

      setAdmin('');
      refetch();
    } catch (err: any) {
      ReactToastify({
        message: err?.data?.log || 'Failed to change HR',
        success: false,
      });
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />
      <ModalContent
        maxH="90vh"
        overflowY="auto"
        borderWidth="0.5px"
        borderColor="#D4D4D4"
        mx={4}
      >
        <ModalHeader
          fontSize="sm"
          color="whiteAlpha.900"
          background="purple.500"
          borderTopRadius="md"
          py={3}
          px={4}
        >
          <div className="flex items-center gap-2">
            <FiUsers size={16} />
            <span>
              {profile_user?.name?.split(' ')[0] || profile_user?.email}&apos;s
              Access
            </span>
          </div>
        </ModalHeader>
        <ModalCloseButton
          color="whiteAlpha.900"
          zIndex={2}
          sx={{
            _hover: {
              bg: 'transparent',
              color: 'white',
            },
          }}
        />
        <ModalBody pb={6}>
          <Box display="flex" flexDirection="column" gap={5}>
            {/* ROLE */}
            <Box>
              <FormLabel htmlFor="role-select" fontSize="13px" fontWeight="bold" mb={1}>
                Role
              </FormLabel>
              <Select
                id="role-select"
                className={selectClasses}
                fontSize="12px"
                onChange={(e) => setRole(e.target.value)}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
              >
                <option>Current Role: {currentRole}</option>
                <option value="Employee">Employee</option>
                <option value="HR">HR</option>
                <option value="TL">Team-Leader</option>
              </Select>
              <Button
                bgGradient="linear(to-r, #6535EB, #9F3198)"
                _hover={{
                  bgGradient: 'linear(to-r, #7F4FF0, #B85AAE)',
                }}
                color="white"
                size="sm"
                mt={3}
                borderRadius="6px"
                onClick={changeRoleHandler}
                isLoading={roleLoading}
              >
                Update Role
              </Button>
            </Box>

            {/* EMPLOYMENT */}
            <Box>
              <FormLabel htmlFor="employment-select" fontSize="13px" fontWeight="bold" mb={1}>
                Employment Type
              </FormLabel>
              <Select
                id="employment-select"
                className={selectClasses}
                fontSize="12px"
                onChange={(e) => setEmploymentType(e.target.value)}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
              >
                <option>Current: {currentEmploymentType}</option>
                <option value="Permanent">Permanent</option>
                <option value="Intern">Intern</option>
              </Select>
              <Button
                bgGradient="linear(to-r, #6535EB, #9F3198)"
                _hover={{
                  bgGradient: 'linear(to-r, #7F4FF0, #B85AAE)',
                }}
                color="white"
                size="sm"
                mt={3}
                borderRadius="6px"
                onClick={changeEmploymentHandler}
                isLoading={empLoading}
              >
                Update Employment
              </Button>
            </Box>

            {/* MANAGER */}
            <Box>
              <FormLabel htmlFor="manager-select" fontSize="13px" fontWeight="bold" mb={1}>
                Manager
              </FormLabel>
              <Select
                id="manager-select"
                className={selectClasses}
                fontSize="12px"
                value={manager}
                onChange={(e) => setManager(e.target.value)}
                border="0.5px solid"
                borderColor="#D4D4D4"
                _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                _hover={{ borderColor: '#D4D4D4' }}
              >
                <option>
                  Current Manager:{' '}
                  {currentManager?.name ||
                    currentManager?.email ||
                    'Not assigned'}
                </option>
                {allEmployees.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </Select>
                <Button
                  bgGradient="linear(to-r, #6535EB, #9F3198)"
                  _hover={{
                    bgGradient: 'linear(to-r, #7F4FF0, #B85AAE)',
                  }}
                  color="white"
                  size="sm"
                  mt={3}
                  borderRadius="6px"
                  onClick={changeManagerHandler}
                  isLoading={managerLoading}
                >
                  Update Manager
                </Button>
             </Box>

             {/* HR */}
             <Box>
                <FormLabel htmlFor="hr-select" fontSize="13px" fontWeight="bold" mb={1}>
                  HR
                </FormLabel>
               <Select
                 id="hr-select"
                 className={selectClasses}
                 fontSize="12px"
                 value={admin}
                 onChange={(e) => setAdmin(e.target.value)}
                 border="0.5px solid"
                 borderColor="#D4D4D4"
                 _focus={{ borderColor: '#D4D4D4', boxShadow: 'none' }}
                 _hover={{ borderColor: '#D4D4D4' }}
               >
                 <option>
                   Current HR:{' '}
                   {currentHR?.name || currentHR?.email || 'Not assigned'}
                 </option>
                 {allEmployees.map((e) => (
                   <option key={e.value} value={e.value}>
                     {e.label}
                   </option>
                 ))}
               </Select>
                 <Button
                  bgGradient="linear(to-r, #6535EB, #9F3198)"
                  _hover={{
                    bgGradient: 'linear(to-r, #7F4FF0, #B85AAE)',
                  }}
                  color="white"
                  size="sm"
                  mt={3}
                  borderRadius="6px"
                  onClick={changeAdminHandler}
                  isLoading={adminLoading}
                >
                  Update HR
                </Button>
             </Box>
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button
            size="md"
            variant="ghost"
            borderRadius="6px"
            border="0.5px solid"
            borderColor="#D4D4D4"
            height={{ base: '26px', md: '34px' }}
            px={{ base: 3, md: 5 }}
            fontSize={{ base: '10px', md: '12px' }}
            fontWeight="500"
            whiteSpace="nowrap"
            _hover={{ bg: 'gray.100', borderColor: '#D4D4D4' }}
            _active={{ transform: 'scale(0.98)' }}
            onClick={handleClose}
          >
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmployeeAccessControl;
